import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Calendar, Users, QrCode, ArrowRight, ChevronRight, Menu, X } from 'lucide-react';
import axios from 'axios';
import BoxSeatLogo from '../components/BoxSeatLogo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/9d09c8fc-6c12-438c-818d-2182801a5113/images/d80c3b9b037099af3cb075a76674db3139058da33147b2dc7861687d0b932c71.png";
const TEXTURE_BG = "https://static.prod-images.emergentagent.com/jobs/9d09c8fc-6c12-438c-818d-2182801a5113/images/6d1cfbfde79926d6d4fd007a1ac5529ca6710336ebae9bda39682abfbd154783.png";

export default function LandingPage() {
  const navigate = useNavigate();
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [venues, setVenues] = useState([]);
  const [meetups, setMeetups] = useState([]);
  const [mobileMenu, setMobileMenu] = useState(false);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/events/upcoming?limit=3`).catch(() => ({ data: [] })),
      axios.get(`${API}/venues`).catch(() => ({ data: [] })),
      axios.get(`${API}/meetups/public?limit=3`).catch(() => ({ data: [] })),
    ]).then(([evtRes, venRes, mtRes]) => {
      setUpcomingEvents(evtRes.data);
      setVenues(venRes.data);
      setMeetups(mtRes.data);
    });
  }, []);

  const formatDate = (d) => {
    const date = new Date(d);
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };
  const formatTime = (d) => {
    const date = new Date(d);
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  return (
    <div className="min-h-screen bg-bg text-white">
      {/* Navigation */}
      <header data-testid="landing-header" className="fixed top-0 w-full z-50 backdrop-blur-2xl bg-black/60 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
            <BoxSeatLogo size="xs" />
          </div>
          <nav className="hidden md:flex items-center gap-8">
            <button onClick={() => navigate('/venues')} className="text-sm font-semibold text-zinc-400 hover:text-white transition-colors tracking-wide uppercase">Venues</button>
            <button onClick={() => navigate('/events')} className="text-sm font-semibold text-zinc-400 hover:text-white transition-colors tracking-wide uppercase">Events</button>
            <button onClick={() => navigate('/meetups')} className="text-sm font-semibold text-zinc-400 hover:text-white transition-colors tracking-wide uppercase">Meetups</button>
          </nav>
          <div className="hidden md:flex items-center gap-3">
            <button
              data-testid="login-nav-btn"
              onClick={() => navigate('/login')}
              className="px-5 py-2 text-sm font-bold uppercase tracking-wide text-white bg-white/5 border border-white/10 rounded-md hover:bg-white/10 transition-all"
            >
              Sign In
            </button>
            <button
              data-testid="register-nav-btn"
              onClick={() => navigate('/register')}
              className="px-5 py-2 text-sm font-bold uppercase tracking-wide text-black bg-primary rounded-md hover:bg-primary-hover transition-all glow-amber"
            >
              Join Now
            </button>
          </div>
          {/* Mobile menu toggle */}
          <button data-testid="mobile-menu-btn" onClick={() => setMobileMenu(!mobileMenu)} className="md:hidden w-10 h-10 flex items-center justify-center text-white">
            {mobileMenu ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
        {/* Mobile dropdown */}
        {mobileMenu && (
          <div className="md:hidden border-t border-white/10 bg-black/90 backdrop-blur-2xl px-6 py-4 space-y-3">
            <button onClick={() => { navigate('/venues'); setMobileMenu(false); }} className="block w-full text-left text-sm font-semibold text-zinc-300 hover:text-white uppercase tracking-wide py-2">Venues</button>
            <button onClick={() => { navigate('/events'); setMobileMenu(false); }} className="block w-full text-left text-sm font-semibold text-zinc-300 hover:text-white uppercase tracking-wide py-2">Events</button>
            <div className="flex gap-3 pt-2">
              <button onClick={() => { navigate('/login'); setMobileMenu(false); }} className="flex-1 py-2 text-sm font-bold uppercase tracking-wide text-white bg-white/5 border border-white/10 rounded-md text-center">Sign In</button>
              <button onClick={() => { navigate('/register'); setMobileMenu(false); }} className="flex-1 py-2 text-sm font-bold uppercase tracking-wide text-black bg-primary rounded-md text-center">Join Now</button>
            </div>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section data-testid="hero-section" className="relative min-h-screen flex items-center pt-16">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-bg via-transparent to-transparent" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <div className="max-w-2xl">
            <p className="text-xs font-bold tracking-[0.3em] uppercase text-primary mb-6 animate-fade-in-up">Philadelphia Sports & Entertainment</p>
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter uppercase leading-[0.9] mb-6 animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
              Your <span className="text-primary">Box Seat</span> to Philly Sports
            </h1>
            <p className="text-lg text-zinc-400 font-medium leading-relaxed mb-10 max-w-lg animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
              Discover venues, browse upcoming events, and organize fan meetups. 
              Tailgates, watch parties, and post-game gatherings — all in one place.
            </p>
            <div className="flex flex-wrap gap-4 animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
              <button
                data-testid="hero-cta-button"
                onClick={() => navigate('/register')}
                className="px-8 py-4 text-sm font-bold uppercase tracking-wide text-black bg-primary rounded-md hover:bg-primary-hover transition-all glow-amber flex items-center gap-2"
              >
                Get Started <ArrowRight size={18} />
              </button>
              <button
                data-testid="hero-browse-btn"
                onClick={() => navigate('/events')}
                className="px-8 py-4 text-sm font-bold uppercase tracking-wide text-white bg-white/5 border border-white/10 rounded-md hover:bg-white/10 transition-all"
              >
                Browse Events
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 md:py-32 relative">
        <div className="absolute inset-0 opacity-10">
          <img src={TEXTURE_BG} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <p className="text-xs font-bold tracking-[0.2em] uppercase text-zinc-500 mb-4">How It Works</p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight uppercase mb-16">
            From the Parking Lot<br/>to the <span className="text-primary">Final Score</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { icon: MapPin, title: "Discover Venues", desc: "Find stadiums, arenas, sports bars, and tailgate spots across Philly." },
              { icon: Calendar, title: "Browse Events", desc: "Phillies, Eagles, Sixers, Union, concerts — see what's coming up." },
              { icon: Users, title: "Create Meetups", desc: "Organize tailgates, watch parties, and post-game gatherings." },
              { icon: QrCode, title: "Share & RSVP", desc: "Generate QR codes, share links, and get your crew together." },
            ].map((f, i) => (
              <div key={i} className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-xl p-8 hover:-translate-y-1 transition-transform duration-300" style={{ animationDelay: `${i * 0.1}s` }}>
                <f.icon size={32} className="text-primary mb-4" />
                <h3 className="font-heading text-xl font-bold uppercase mb-2">{f.title}</h3>
                <p className="text-sm text-zinc-400 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Upcoming Events */}
      {upcomingEvents.length > 0 && (
        <section className="py-24 md:py-32 bg-surface">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="flex items-end justify-between mb-12">
              <div>
                <p className="text-xs font-bold tracking-[0.2em] uppercase text-zinc-500 mb-4">Coming Up</p>
                <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight uppercase">
                  Upcoming <span className="text-primary">Events</span>
                </h2>
              </div>
              <button onClick={() => navigate('/events')} className="text-sm text-primary font-bold uppercase flex items-center gap-1 hover:gap-2 transition-all">
                View All <ChevronRight size={16} />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {upcomingEvents.map((evt) => (
                <div key={evt.id} data-testid="event-card" className="bg-bg border border-white/10 rounded-xl overflow-hidden hover:-translate-y-1 transition-transform duration-300 cursor-pointer" onClick={() => navigate('/events')}>
                  <div className="p-6">
                    <span className="text-xs font-bold tracking-[0.15em] uppercase text-primary">{evt.event_type} / {evt.category}</span>
                    <h3 className="font-heading text-xl font-bold uppercase mt-2 mb-3">{evt.title}</h3>
                    <div className="space-y-2 text-sm text-zinc-400">
                      <div className="flex items-center gap-2"><Calendar size={14} className="text-zinc-500" /> {formatDate(evt.date_time)} at {formatTime(evt.date_time)}</div>
                      <div className="flex items-center gap-2"><MapPin size={14} className="text-zinc-500" /> {evt.venue_name}</div>
                      {evt.price_range && <div className="text-primary font-semibold">{evt.price_range}</div>}
                    </div>
                    {evt.teams?.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-4">
                        {evt.teams.map((t) => <span key={t} className="px-2 py-1 text-xs bg-white/5 border border-white/10 rounded-full">{t}</span>)}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Venues Preview */}
      {venues.length > 0 && (
        <section className="py-24 md:py-32">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="flex items-end justify-between mb-12">
              <div>
                <p className="text-xs font-bold tracking-[0.2em] uppercase text-zinc-500 mb-4">Explore</p>
                <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight uppercase">
                  Philadelphia <span className="text-primary">Venues</span>
                </h2>
              </div>
              <button onClick={() => navigate('/venues')} className="text-sm text-primary font-bold uppercase flex items-center gap-1 hover:gap-2 transition-all">
                View All <ChevronRight size={16} />
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {venues.slice(0, 3).map((v) => (
                <div key={v.id} data-testid="venue-card" className="bg-surface border border-white/10 rounded-xl overflow-hidden hover:-translate-y-1 transition-transform duration-300 cursor-pointer" onClick={() => navigate('/venues')}>
                  {v.image_url && <img src={v.image_url} alt={v.name} className="w-full h-40 object-cover" />}
                  <div className="p-6">
                    <span className="text-xs font-bold tracking-[0.15em] uppercase text-zinc-500">{v.venue_type}</span>
                    <h3 className="font-heading text-xl font-bold uppercase mt-1 mb-2">{v.name}</h3>
                    <p className="text-sm text-zinc-400 line-clamp-2">{v.description}</p>
                    <div className="flex items-center gap-2 mt-3 text-xs text-zinc-500">
                      <MapPin size={12} /> {v.city}, {v.state}
                      {v.capacity && <span className="ml-auto"><Users size={12} className="inline mr-1" />{v.capacity.toLocaleString()}</span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Meetups Preview */}
      {meetups.length > 0 && (
        <section className="py-24 md:py-32 bg-surface">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="flex items-end justify-between mb-12">
              <div>
                <p className="text-xs font-bold tracking-[0.2em] uppercase text-zinc-500 mb-4">Get Together</p>
                <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight uppercase">
                  Fan <span className="text-primary">Meetups</span>
                </h2>
              </div>
              <button onClick={() => navigate('/login')} className="text-sm text-primary font-bold uppercase flex items-center gap-1 hover:gap-2 transition-all">
                Sign In to Join <ChevronRight size={16} />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {meetups.map((m) => (
                <div key={m.id} data-testid="meetup-card" className="bg-bg border border-white/10 rounded-xl p-6 hover:-translate-y-1 transition-transform duration-300">
                  <h3 className="font-heading text-lg font-bold uppercase mb-2">{m.title}</h3>
                  <p className="text-sm text-zinc-400 line-clamp-2 mb-4">{m.description}</p>
                  <div className="space-y-2 text-xs text-zinc-500">
                    <div className="flex items-center gap-2"><Calendar size={12} /> {formatDate(m.date)} at {m.time}</div>
                    <div className="flex items-center gap-2"><MapPin size={12} /> {m.location}</div>
                    <div className="flex items-center gap-2"><Users size={12} /> {m.participant_count || 0} attending</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-24 md:py-32 relative">
        <div className="absolute inset-0 opacity-5">
          <img src={TEXTURE_BG} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight uppercase mb-6">
            Ready for Your <span className="text-primary">Box Seat</span>?
          </h2>
          <p className="text-lg text-zinc-400 mb-10">Join the Philadelphia fan community. It's free.</p>
          <button
            data-testid="cta-register-btn"
            onClick={() => navigate('/register')}
            className="px-10 py-4 text-sm font-bold uppercase tracking-wide text-black bg-primary rounded-md hover:bg-primary-hover transition-all glow-amber"
          >
            Create Your Account
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10 py-12">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <BoxSeatLogo size="xs" onClick={() => navigate('/')} />
              <span className="text-xs text-zinc-600">Philadelphia Sports & Entertainment</span>
            </div>
            <div className="flex items-center gap-6 text-xs text-zinc-500">
              <button onClick={() => navigate('/terms')} className="hover:text-white transition-colors">Terms of Service</button>
              <span>BOX SEAT&trade; USPTO App. Nos. 97896710, 97896700</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
